import { useEffect, useState } from 'react';
import { Banknote } from 'lucide-react';
import Modal from '../../shared/Modal';
import ModalFormActions from '../../shared/ModalFormActions';
import { TextInput, SelectInput } from '../../shared/FormControls';
import { useActiveCurrencyTypes } from '../../../hooks/admin/financeSettings/currencyTypes/useActiveCurrencyTypes';
import { useActiveFundCommitments } from '../../../hooks/finance/useActiveFundCommitments';

const initialFormState = {
  amount: '',
  currency: '',
  date: '',
  fundCommitment: '',
  paymentProof: '',
};

const getFundCommitmentLabel = (fc) => {
  if (!fc) return '';
  const amt = fc.amount != null ? fc.amount : '';
  const proj = fc.project?.name || fc.project?.projectCode || '';
  const contract = fc.contract?.contractNo || fc.contract?.contractName || '';
  const partner = fc.partner?.name || '';
  return [amt, proj, contract, partner].filter(Boolean).join(' - ') || fc._id;
};

const FundDisbursementFormModal = ({
  isOpen,
  mode = 'create',
  fundDisbursement,
  onSubmit,
  onClose,
  isSubmitting = false,
  serverError,
}) => {
  const [formValues, setFormValues] = useState(initialFormState);
  const [errors, setErrors] = useState({});

  const { currencyTypes } = useActiveCurrencyTypes({ enabled: isOpen });
  const { fundCommitments } = useActiveFundCommitments(
    { page: 1, pageSize: 200 },
    { enabled: isOpen }
  );

  useEffect(() => {
    if (isOpen) {
      setErrors({});

      if (mode === 'edit' && fundDisbursement) {
        setFormValues({
          amount: fundDisbursement.amount?.toString() ?? '',
          currency: fundDisbursement.currency?._id || fundDisbursement.currency || '',
          date: fundDisbursement.date
            ? new Date(fundDisbursement.date).toISOString().slice(0, 10)
            : '',
          fundCommitment: fundDisbursement.fundCommitment?._id || fundDisbursement.fundCommitment || '',
          paymentProof: fundDisbursement.paymentProof ?? '',
        });
      } else {
        setFormValues(initialFormState);
      }
    }
  }, [isOpen, mode, fundDisbursement]);

  const validate = () => {
    const nextErrors = {};

    if (!formValues.amount || isNaN(parseFloat(formValues.amount))) {
      nextErrors.amount = 'Valid amount is required';
    }

    if (!formValues.currency) {
      nextErrors.currency = 'Currency is required';
    }

    if (!formValues.date) {
      nextErrors.date = 'Date is required';
    }

    if (!formValues.fundCommitment) {
      nextErrors.fundCommitment = 'Fund commitment is required';
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;

    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!validate()) return;

   // console.log(formValues);

    const payload = {
      amount: parseFloat(formValues.amount),
      currency: formValues.currency,
      date: formValues.date,
      fundCommitment: formValues.fundCommitment,
      paymentProof: formValues.paymentProof.trim() || undefined,
    };

    onSubmit({ mode, payload });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'edit' ? 'Edit Fund Disbursement' : 'Create Fund Disbursement'}
      size="2xl"
      icon={
        <Banknote
          className="h-6 w-6 text-primary-dark"
          strokeWidth={1.5}
        />
      }
      footer={
        <ModalFormActions
          onCancel={onClose}
          formId="fund-disbursement-form"
          isSubmitting={isSubmitting}
          submitText={mode === 'edit' ? 'Save changes' : 'Create Fund Disbursement'}
        />
      }
    >
      <form
        id="fund-disbursement-form"
        className="space-y-4"
        onSubmit={handleSubmit}
      >
        {serverError ? (
          <p className="rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700">
            {serverError}
          </p>
        ) : null}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <TextInput
            id="amount"
            label="Amount"
            type="number"
            step="0.01"
            min="0"
            value={formValues.amount}
            onChange={handleChange('amount')}
            placeholder="0"
            disabled={isSubmitting}
            error={errors.amount}
            required
          />

          <SelectInput
            id="currency"
            label="Currency"
            value={formValues.currency}
            onChange={handleChange('currency')}
            disabled={isSubmitting}
            error={errors.currency}
            required
          >
            <option value="">Select currency</option>
            {currencyTypes.map((c) => (
              <option key={c._id} value={c._id}>
                {c.code} - {c.name}
              </option>
            ))}
          </SelectInput>

          <TextInput
            id="date"
            label="Date"
            type="date"
            value={formValues.date}
            onChange={handleChange('date')}
            disabled={isSubmitting}
            error={errors.date}
            required
          />
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <SelectInput
            id="fundCommitment"
            label="Fund Commitment"
            value={formValues.fundCommitment}
            onChange={handleChange('fundCommitment')}
            disabled={isSubmitting}
            error={errors.fundCommitment}
            required
          >
            <option value="">Select fund commitment</option>
            {fundCommitments?.map((fc) => (
              <option key={fc._id} value={fc._id}>
                {fc.amount} - {fc.partner.name}
              </option>
            ))}
          </SelectInput>

          <TextInput
            id="paymentProof"
            label="Payment Proof"
            type="text"
            value={formValues.paymentProof}
            onChange={handleChange('paymentProof')}
            placeholder="Reference, URL, or proof identifier"
            maxLength={500}
            disabled={isSubmitting}
            error={errors.paymentProof}
          />
        </div>
      </form>
    </Modal>
  );
};

export default FundDisbursementFormModal;
